// Panel de navegación del Admin, anclado a la entrada "Administración" del Sidebar principal en
// vez de vivir como una segunda columna fija dentro de AdminView (ver
// src/views/AdminView/index.tsx, que a partir de la Task 12 solo dibuja el contenido). 3
// secciones colapsables (Admin General / Pulso / Brillamos) — con una sola sección visible en
// total no hay nada que colapsar, así que se muestra la lista plana sin acordeón (ver
// `showHeaders` más abajo; el criterio es sobre el número de secciones visibles, no sobre cuántos
// ítems tiene cada una).
//
// Ver docs/superpowers/specs/2026-08-04-menu-unificado-design.md, Fase 3.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageSpinner } from '@/components/common';
import { useAdminPanelContext, type PanelTab } from '@/contexts/AdminPanelContext';
import { useAdminMenuSections, type AdminMenuSection } from './useAdminMenuSections';

const COLLAPSED_STORAGE_PREFIX = 'admin-flyout-section-collapsed:';

function readSectionCollapsed(sectionId: string): boolean {
  try {
    return localStorage.getItem(COLLAPSED_STORAGE_PREFIX + sectionId) === 'true';
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('AdminFlyout: no se pudo leer el estado de colapso persistido', error);
    }
    return false;
  }
}

function writeSectionCollapsed(sectionId: string, collapsed: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_STORAGE_PREFIX + sectionId, String(collapsed));
  } catch (error) {
    // Almacenamiento no disponible (modo privado, cuota excedida, etc.): el colapso no persiste
    // entre sesiones, pero la app sigue funcionando con el estado en memoria.
    if (import.meta.env.DEV) {
      console.error('AdminFlyout: no se pudo persistir el estado de colapso', error);
    }
  }
}

interface SectionGroupProps {
  section: AdminMenuSection;
  showHeader: boolean;
  activeTab: PanelTab;
  onSelect: (tab: PanelTab) => void;
}

function SectionGroup({ section, showHeader, activeTab, onSelect }: SectionGroupProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(() => readSectionCollapsed(section.id));

  function toggle() {
    setCollapsed(prev => {
      const next = !prev;
      writeSectionCollapsed(section.id, next);
      return next;
    });
  }

  return (
    <div className="admin-flyout-section">
      {showHeader && (
        <button
          type="button"
          className="admin-flyout-section-header"
          onClick={toggle}
          aria-expanded={!collapsed}
        >
          <span>{t(section.titleKey)}</span>
          <i className={`fas fa-chevron-${collapsed ? 'right' : 'down'}`} aria-hidden="true" />
        </button>
      )}
      {(!showHeader || !collapsed) && (
        <div className="admin-flyout-section-items">
          {section.items.map(item => (
            <button
              key={item.tab}
              type="button"
              className={`admin-flyout-item${activeTab === item.tab ? ' active' : ''}`}
              onClick={() => onSelect(item.tab)}
            >
              <i className={`fas ${item.icon}`} aria-hidden="true" />
              <span>{t(item.labelKey, { ns: item.i18nNamespace })}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface AdminFlyoutProps {
  /** Cierra el overlay al elegir una sección — solo debe activarse desde el consumidor móvil
   *  (`MobileNav.tsx`). En escritorio (`Sidebar.tsx`) el flyout es un popover lateral pequeño y
   *  debe seguir abierto tras elegir (comportamiento actual, sin cambios); en móvil es un overlay
   *  a pantalla completa (`z-index: 400`, ver Sidebar.css @media max-width:768px) que tapa el
   *  contenido recién elegido si no se cierra solo, y además ese mismo overlay intercepta el toque
   *  sobre el botón "Administración" del nav inferior (`z-index: 200`) — sin este auto-cierre, la
   *  única forma de cerrarlo en un dispositivo real es el botón "×" propio del overlay. */
  shouldCloseOnSelect?: boolean;
}

export function AdminFlyout({ shouldCloseOnSelect = false }: AdminFlyoutProps) {
  const { t } = useTranslation();
  const { sections, profileResolved } = useAdminMenuSections();
  const { activeTab, setActiveTab, setIsAdminFlyoutOpen } = useAdminPanelContext();
  // Con una sola sección visible no hay nada que colapsar: se muestra la lista plana, sin
  // encabezado de acordeón (mismo criterio que el spec dio para el Sidebar cuando el usuario
  // tiene un solo módulo).
  const showHeaders = sections.length > 1;

  function handleSelect(tab: PanelTab) {
    setActiveTab(tab);
    if (shouldCloseOnSelect) {
      setIsAdminFlyoutOpen(false);
    }
  }

  return (
    <div className="admin-flyout">
      {/* Solo visible en la variante overlay móvil (CSS, ver Sidebar.css @media max-width:768px)
          — en escritorio el flyout ya se cierra con clic fuera (LanguageMenu / patrón
          `adminLinkRef`), y un overlay a pantalla completa no tiene "fuera" al que hacer clic, así
          que necesita un cierre explícito. Fix de hueco encontrado en verificación manual de
          Task 12. */}
      <button
        type="button"
        className="admin-flyout-close"
        onClick={() => setIsAdminFlyoutOpen(false)}
        aria-label={t('common.close')}
      >
        <i className="fas fa-times" aria-hidden="true" />
      </button>
      {/* BUG QA — "línea blanca" intermitente: sin `profileResolved`, `sections` sale vacío (rol de
          fallback = Employee en producción, ver `useAdminMenuSections`) y esta caja se quedaba sin
          nada que pintar hasta que el perfil real resolvía, unos segundos después — un hueco en
          blanco indistinguible de un menú roto. Mientras tanto se muestra una carga explícita. */}
      {!profileResolved ? (
        <PageSpinner minHeight="56px" />
      ) : (
        sections.map(section => (
          <SectionGroup
            key={section.id}
            section={section}
            showHeader={showHeaders}
            activeTab={activeTab}
            onSelect={handleSelect}
          />
        ))
      )}
    </div>
  );
}
